'use client'

import { useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBrowserNavigation, reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  RefundOfferAction,
  handleRefundOffer,
  getRefundOfferId,
  transformRefundOfferIdDto,
  partialRefundOffersClickEvent,
} from '@marketplace-web/complaints/complaints-data'

import GenericErrorModal from '../GenericErrorModal'
import CancelPartialRefundOfferDialog from './CancelPartialRefundOfferDialog'

enum CancelPartialRefundOfferActiveDialog {
  CancelPartialRefundDialog,
  ErrorDialog,
  None,
}

type OwnProps = {
  url?: string
  complaintRefundOfferId?: number
  complaintId?: string | null
  transactionId?: string
  onErrorDialogClose?: () => void
  onUrlNavigation?: () => void
  onCancel?: () => void
}

type Props = Partial<ConversationActionProps> & OwnProps

const CancelPartialRefundOffer = ({
  primary,
  title,
  inverse,
  theme,
  size,
  complaintRefundOfferId,
  url,
  complaintId,
  transactionId,
  onErrorDialogClose,
  onUrlNavigation,
  onCancel,
}: Props) => {
  const { track } = useTracking()
  const { safeBack } = useBrowserNavigation()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeDialog, setActiveDialog] = useState(CancelPartialRefundOfferActiveDialog.None)

  const {
    fetch: fetchRefundOfferId,
    transformedData: fetchedRefundOfferId,
    isLoading: isRefundOfferIdLoading,
  } = useFetch(getRefundOfferId, response => transformRefundOfferIdDto(response)?.refundOfferId)

  const refundOfferId = complaintRefundOfferId || fetchedRefundOfferId
  const handleDialogClose = () => {
    setActiveDialog(CancelPartialRefundOfferActiveDialog.None)
  }

  useEffect(() => {
    if (
      activeDialog !== CancelPartialRefundOfferActiveDialog.CancelPartialRefundDialog ||
      complaintRefundOfferId ||
      !complaintId
    )
      return

    fetchRefundOfferId(Number(complaintId))
  }, [fetchRefundOfferId, activeDialog, complaintId, complaintRefundOfferId])

  const handleErrorDialogClose = () => {
    onErrorDialogClose?.()
    handleDialogClose()

    if (url) {
      onUrlNavigation?.()
      safeBack(url)

      return
    }

    reloadPage()
  }

  const openCancelPartialRefundOfferDialog = () => {
    setActiveDialog(CancelPartialRefundOfferActiveDialog.CancelPartialRefundDialog)
  }

  const trackCancelOfferClick = () => {
    if (!transactionId || !complaintId) return

    const targetDetails = JSON.stringify({
      refund_offer_id: refundOfferId?.toString(),
    })

    track(
      partialRefundOffersClickEvent({
        target: 'partial_refund_offer_cancel',
        transactionId,
        complaintId,
        targetDetails,
      }),
    )
  }

  const cancelPartialRefundOffer = async () => {
    onCancel?.()
    if (isSubmitting || !refundOfferId) return

    setIsSubmitting(true)

    const response = await handleRefundOffer({
      refundOfferId,
      action: RefundOfferAction.Cancel,
    })

    trackCancelOfferClick()

    if ('errors' in response) {
      setIsSubmitting(false)
      setActiveDialog(CancelPartialRefundOfferActiveDialog.ErrorDialog)

      return
    }

    setIsSubmitting(false)

    handleDialogClose()
    if (url) {
      onUrlNavigation?.()
      safeBack(url)
    }
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size || 'medium'}
        theme={theme}
        inverse={inverse}
        onClick={openCancelPartialRefundOfferDialog}
        testId="cancel-partial-refund-offer-conversation-action"
      />
      <CancelPartialRefundOfferDialog
        refundOfferId={refundOfferId}
        isDataLoading={isRefundOfferIdLoading}
        isOpen={activeDialog === CancelPartialRefundOfferActiveDialog.CancelPartialRefundDialog}
        onSubmit={cancelPartialRefundOffer}
        onClose={handleDialogClose}
      />
      <GenericErrorModal
        isShown={activeDialog === CancelPartialRefundOfferActiveDialog.ErrorDialog}
        onClose={handleErrorDialogClose}
      />
    </>
  )
}

export default CancelPartialRefundOffer
